import { t, Trans } from "@lingui/macro";
import { ArrowsIn, ArrowsOut, CircleNotch } from "@phosphor-icons/react";
import type { ResumeDto } from "@stylingcv/dto";
import { useBreakpoint } from "@stylingcv/hooks";
import { defaultResumeData, getSampleResumeForLocale } from "@stylingcv/schema";
import { Button } from "@stylingcv/ui";
import { pageSizeMap, templatesList } from "@stylingcv/utils";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router-dom";
import { redirect, useParams, useSearchParams } from "react-router-dom";
import { addLocaleToPath } from "@/client/utils/url-locales";
import {
  applyPendingSummaryToResumeDto,
  PENDING_SUMMARY_SHOW_TOAST_KEY,
} from "@/client/constants/pending-summary";

import { useToast } from "@/client/hooks/use-toast";
import { useUserAccess } from "@/client/hooks/use-user-access";
import { queryClient } from "@/client/libs/query-client";
import { DeleteSectionDialog } from "@/client/pages/builder/sidebars/left/dialogs/delete-section";
import { GuestSessionService } from "@/client/services/guest-session";
import { findResumeById } from "@/client/services/resume";
import { printResume } from "@/client/services/resume";
import { useAuthStore } from "@/client/stores/auth";
import { useBuilderStore } from "@/client/stores/builder";
import { migrateSignature, useResumeStore } from "@/client/stores/resume";
import { GuestResumeSync } from "@/client/utils/guest-resume-sync";
import { trackDownloadResume } from "@/client/utils/gtm";

const LOCAL_STORAGE_KEY = "guest-resume";

// Hook to calculate responsive scale for mobile preview - IMPROVED
const useResponsiveScale = () => {
  const [scale, setScale] = useState(0.8); // Better default for mobile
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Only subscribe to the specific primitive value we need to prevent unnecessary re-renders
  const pageFormat = useResumeStore((state) => state.resume.data?.metadata?.page?.format) || "a4";

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) {
        // Retry if container not ready
        setTimeout(updateScale, 50);
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width - 24;

      // Ensure we have valid dimensions
      if (containerWidth <= 0) {
        setTimeout(updateScale, 100);
        return;
      }

      // Get resume page format with comprehensive fallback
      const pageData = pageSizeMap[pageFormat];

      if (!pageData) {
        console.warn("⚠️ Invalid page format:", pageFormat, "- using fallback scale");
        setScale(0.7);
        setIsReady(true);
        return;
      }

      const resumeWidth = pageData.width * 3.78; // MM_TO_PX
      console.log("📐 Scale calculation:", { containerWidth, resumeWidth });

      // Calculate responsive scale with better safeguards
      const calculatedScale = Math.min(containerWidth / resumeWidth, 1);
      const finalScale = Math.max(0.4, Math.min(calculatedScale, 1)); // Wider range for small screens

      // Validate scale is a valid number
      if (isNaN(finalScale) || finalScale <= 0) {
        console.warn("⚠️ Invalid scale calculated, using fallback:", finalScale);
        setScale(0.7);
      } else {
        console.log("✅ Scale set to:", finalScale);
        setScale(finalScale);
      }

      setIsReady(true);
    };

    // Initial calculation with retries for mobile
    updateScale();

    // Multiple retries for mobile devices that mount slowly
    const timeouts = [100, 250, 500, 1000].map((delay) => setTimeout(updateScale, delay));

    // Resize handler with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateScale, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [pageFormat]);

  return {
    scale: isReady ? scale : 0.8, // Use safe default until ready
    containerRef,
    isReady,
  };
};

// Map short lang codes from URL params to full locale codes
const mapLangParam = (lang: string | null): string => {
  if (!lang) {
    // Fallback: extract locale from URL path (e.g., /ar-SA/builder/try → ar-SA)
    const pathLocale = window.location.pathname.split("/")[1];
    if (pathLocale && pathLocale.includes("-")) return pathLocale;
    // Try mapping short codes from path
    const mapping: Record<string, string> = {
      ar: "ar-SA", es: "es-ES", de: "de-DE", fr: "fr-FR",
      en: "en-US", pt: "pt-BR", tl: "tl-PH", hi: "hi-IN",
    };
    if (pathLocale && mapping[pathLocale.toLowerCase()]) return mapping[pathLocale.toLowerCase()];
    return "en-US";
  }
  const mapping: Record<string, string> = {
    ar: "ar-SA", es: "es-ES", de: "de-DE", fr: "fr-FR",
    en: "en-US", pt: "pt-BR", tl: "tl-PH", hi: "hi-IN",
  };
  return mapping[lang.toLowerCase()] ?? lang;
};

const DEFAULT_TEMPLATE = "vibrant";

// Validate template name against available templates, fallback to default
const validateTemplate = (template: string | null): string | null => {
  if (!template) return null;
  const isValid = (templatesList as readonly string[]).includes(template.toLowerCase());
  if (!isValid) {
    console.log("⚠️ Template not found:", template, "→ using default:", DEFAULT_TEMPLATE);
    return DEFAULT_TEMPLATE;
  }
  return template.toLowerCase();
};

// Ensure a complete resume structure for guest users
const createGuestResume = (data: any = defaultResumeData): ResumeDto => {
  // Check for template and data parameters in URL
  const urlParams = new URLSearchParams(window.location.search);
  const templateParam = validateTemplate(urlParams.get("template"));
  const dataParam = urlParams.get("data");
  const langParam = urlParams.get("lang");

  // If data=1, use localized sample data as the base instead of defaultResumeData
  let baseData = data;
  if (dataParam === "1" && data === defaultResumeData) {
    const locale = mapLangParam(langParam);
    console.log("📋 Loading sample data for locale:", locale);
    baseData = getSampleResumeForLocale(locale);
  }

  // If data already looks like a ResumeDto, return as-is but apply template if provided
  if (baseData?.id && baseData.title && baseData.slug && baseData.data) {
    const resume = baseData as ResumeDto;
    if (templateParam) {
      console.log("🎨 Applying template to existing resume:", templateParam);
      resume.data.metadata.template = templateParam;
    }
    return resume;
  }

  // Create metadata with template parameter if provided
  const metadata = {
    ...defaultResumeData.metadata,
    ...baseData?.metadata,
    ...(templateParam && { template: templateParam }),
  };

  if (templateParam) {
    console.log("🎨 Creating guest resume with template:", templateParam);
  }

  // Otherwise, wrap in ResumeDto structure
  return {
    id: "guest-resume",
    userId: "guest",
    title: t`Guest Resume`,
    slug: "guest-resume",
    visibility: "private",
    locked: false,
    type: "resume" as const,
    createdAt: new Date(),
    updatedAt: new Date(),
    data: {
      ...defaultResumeData,
      ...baseData,
      sections: baseData?.sections ? baseData.sections : { ...defaultResumeData.sections },
      basics: baseData?.basics ? baseData.basics : { ...defaultResumeData.basics },
      metadata,
    },
  };
};

// Responsive Preview Component
const ResponsivePreview = ({
  frameRef,
  setFrameRef,
  resumeId,
}: {
  frameRef: HTMLIFrameElement | null;
  setFrameRef: (ref: HTMLIFrameElement | null) => void;
  resumeId: string;
}) => {
  const { isMobile, isTablet } = useBreakpoint();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { scale, containerRef, isReady } = useResponsiveScale();
  const isRTL = document.documentElement.dir === "rtl";
  const { toast } = useToast();

  // PDF Export mutation
  const { mutateAsync: printResumeFn, isPending: isPrinting } = useMutation({
    mutationFn: printResume,
    onError: (error: any) => {
      toast({
        variant: "error",
        title: t`Failed to export PDF`,
        description:
          error?.message || t`An error occurred while generating the PDF. Please try again.`,
      });
    },
  });

  const onPrintPDF = async () => {
    try {
      const { url } = await printResumeFn({ id: resumeId });

      // Force download the PDF
      const link = document.createElement("a");
      link.href = url;
      link.download = `resume-${Date.now()}.pdf`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.append(link);
      link.click();
      link.remove();

      const templateName = useResumeStore.getState().resume?.data?.metadata?.template;
      trackDownloadResume({
        resumeId,
        format: "pdf",
        templateName,
        outcome: "success",
      });

      toast({
        title: t`PDF Downloaded`,
        description: t`Your resume has been successfully exported as PDF.`,
      });
    } catch (error) {
      console.error("PDF export error:", error);
    }
  };

  // Mobile: Compact preview with responsive scaling
  if (isMobile && !isFullScreen) {
    return (
      <div className="relative flex h-full flex-col overflow-hidden rounded-lg bg-secondary-accent/20">
        {/* Responsive Scaled Preview - Full Height */}
        <div ref={containerRef} className="relative flex-1 overflow-hidden bg-white">
          {/* Floating Full Screen Button */}
          <Button
            size="sm"
            variant="outline"
            className="absolute bottom-3 right-3 z-10 h-8 rounded-full border-gray-300 bg-white/90 px-3 text-gray-700 shadow-md backdrop-blur-sm hover:bg-gray-100"
            onClick={() => {
              setIsFullScreen(true);
            }}
          >
            <ArrowsOut className="mr-1.5 size-3.5" />
            <Trans>Full Screen</Trans>
          </Button>
          {!isReady && (
            <div className="flex h-full items-center justify-center">
              <CircleNotch className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}
          <iframe
            ref={setFrameRef}
            title={resumeId || "guest-resume"}
            src="/artboard/builder"
            className={`size-full transition-opacity duration-300 ${
              isReady ? "opacity-100" : "opacity-0"
            }`}
            style={{
              transform: `scale(${Math.max(0.4, scale)})`,
              transformOrigin: isRTL ? "100% 0" : "0 0",
              width: `${100 / Math.max(0.4, scale)}%`,
              height: `${100 / Math.max(0.4, scale)}%`,
            }}
            onLoad={(e) => {
              console.log("📱 Mobile iframe loaded");
              const iframe = e.target as HTMLIFrameElement;
              setTimeout(() => {
                iframe.contentWindow?.postMessage({ type: "FORCE_RENDER" }, "*");
              }, 100);
            }}
          />

          {/* Overlay for better touch handling */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/10 to-transparent" />
        </div>
      </div>
    );
  }

  // Mobile: Full screen preview with responsive scaling
  if (isMobile && isFullScreen) {
    return (
      <div className="fixed inset-0 z-[90] bg-background">
        {/* Full Screen Header */}
        <div className="flex items-center justify-between border-b border-border/50 bg-white p-4">
          <span className="text-lg font-semibold text-gray-800">
            <Trans>
              <Trans>Resume Preview</Trans>
            </Trans>
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-9 px-4 text-gray-700 border-gray-300"
              onClick={() => {
                setIsFullScreen(false);
              }}
            >
              <ArrowsIn className="mr-2 size-4" />
              <Trans>Close</Trans>
            </Button>
          </div>
        </div>

        {/* Full Screen Responsive Iframe */}
        <div
          ref={containerRef}
          className="relative w-full"
          style={{ height: "calc(100vh - 73px)" }}
        >
          {!isReady && (
            <div className="flex h-full items-center justify-center">
              <CircleNotch className="size-8 animate-spin text-muted-foreground" />
            </div>
          )}
          <iframe
            ref={setFrameRef}
            title={resumeId || "guest-resume"}
            src="/artboard/builder"
            className={`size-full transition-opacity duration-300 ${
              isReady ? "opacity-100" : "opacity-0"
            }`}
            style={{
              transform: `scale(${Math.max(0.4, scale)})`,
              transformOrigin: isRTL ? "100% 0" : "0 0",
              width: `${100 / Math.max(0.4, scale)}%`,
              height: `${100 / Math.max(0.4, scale)}%`,
            }}
            onLoad={(e) => {
              console.log("🖥️ Fullscreen iframe loaded");
              const iframe = e.target as HTMLIFrameElement;
              setTimeout(() => {
                iframe.contentWindow?.postMessage({ type: "FORCE_RENDER" }, "*");
              }, 100);
            }}
          />
        </div>
      </div>
    );
  }

  // Tablet: Responsive preview with better sizing
  if (isTablet) {
    return (
      <div className="relative h-full bg-secondary-accent/20">
        <iframe
          ref={setFrameRef}
          title={resumeId || "guest-resume"}
          src="/artboard/builder"
          className="size-full"
        />
      </div>
    );
  }

  // Desktop: Original behavior
  return (
    <iframe
      ref={setFrameRef}
      title={resumeId || "guest-resume"}
      src="/artboard/builder"
      className="mt-0 w-full"
      style={{ height: `calc(100vh - 64px)` }}
    />
  );
};

export const BuilderPage = () => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);
  const setIsArtboardLoadingOnMount = useBuilderStore((state) => state.setIsArtboardLoading);
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const params = useParams();

  const resume = useResumeStore((state) => state.resume);
  const title = useResumeStore((state) => state.resume.title || t`Guest Resume`);
  const setValue = useResumeStore((state) => state.setValue);
  const { isMobile } = useBreakpoint();
  const { userType } = useUserAccess(); // Get user type for watermark
  const { toast } = useToast(); // Add toast hook for resume prefilling

  // Toast after Summary tool injected HTML via sessionStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(PENDING_SUMMARY_SHOW_TOAST_KEY) !== "1") return;
    sessionStorage.removeItem(PENDING_SUMMARY_SHOW_TOAST_KEY);
    toast({
      title: t`Summary inserted`,
      description: t`Your generated summary was added to the Summary section.`,
    });
  }, [toast]);

  // Reset loading state when component mounts (navigating to a new resume)
  useEffect(() => {
    setIsArtboardLoadingOnMount(true);
  }, [params.id, setIsArtboardLoadingOnMount]);

  // Read template from URL once at component level (validated against available templates)
  const [searchParams] = useSearchParams();
  const templateParam = validateTemplate(searchParams.get("template"));

  // Handle template parameter from URL - run after resume is loaded
  useEffect(() => {
    if (templateParam && resume.id && resume.data?.metadata) {
      const currentTemplate = resume.data.metadata.template;
      console.log("🎨 Template parameter detected:", templateParam);
      console.log("🎨 Current template:", currentTemplate);

      // ALWAYS apply template from URL parameter (don't check if it's different)
      // This ensures the URL parameter takes precedence over localStorage
      console.log("🎨 Applying template from URL:", templateParam);
      setValue("data.metadata.template", templateParam);
    }
  }, [templateParam, setValue, resume.id]);

  // Handle resume prefilling from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resumePrefilled = params.get("resume_prefilled");
    const resumeBase64 = params.get("resume_base64");
    const jobTitle = params.get("job_title");

    if (resumePrefilled === "true" && resumeBase64) {
      handlePrefilledResume(resumeBase64, jobTitle);
    }
  }, []); // Run once on mount

  // Function to handle prefilled resume from URL
  const handlePrefilledResume = async (base64: string, jobTitle?: string | null) => {
    try {
      // Decode Base64
      const decodedUri = decodeURIComponent(base64);
      const resumeText = atob(decodedUri);

      // Call backend to get language detection and onboarding message
      const response = await fetch("/api/resume/load-prefilled", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resumeText,
          jobTitle: jobTitle || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to load resume");
      }

      const data = await response.json();

      // Show onboarding message as toast
      toast({
        title: data.language === "ar" ? "تم التحميل بنجاح" : "Resume Loaded",
        description: data.onboardingMessage,
        duration: 8000,
      });

      // Populate builder with parsed data
      if (data.parsedData) {
        console.log("Parsed resume data:", data.parsedData);

        // Merge parsed data with default schema to ensure all required fields exist
        const currentResume = useResumeStore.getState().resume;

        // Helper to merge sections safely
        const mergeSections = (parsed: any) => {
          const merged = { ...defaultResumeData.sections };

          if (parsed.sections) {
            for (const key of Object.keys(parsed.sections)) {
              const sectionKey = key as keyof typeof merged;
              if (merged[sectionKey]) {
                merged[sectionKey] = {
                  ...merged[sectionKey],
                  ...parsed.sections[key],
                };
              }
            }
          }

          return merged;
        };

        // Update resume store with safely merged data
        const updatedResume = {
          ...currentResume,
          data: {
            ...currentResume.data,
            basics: {
              ...defaultResumeData.basics,
              ...currentResume.data.basics,
              ...data.parsedData.basics,
            },
            sections: mergeSections(data.parsedData),
            metadata: {
              ...currentResume.data.metadata,
              locale: data.language === "ar" ? "ar" : "en",
            },
          },
        };

        useResumeStore.setState({ resume: updatedResume });
        console.log("✅ Builder populated with resume data");
      }
    } catch (error) {
      console.error("Failed to decode resume:", error);
      toast({
        variant: "error",
        title: "Could not load resume",
        description: "Please paste your resume manually.",
      });
    }
  };

  // Check if this is a guest resume with specific ID
  const isGuestResumeWithId = window.location.pathname.startsWith("/builder/guest/");
  const guestResumeId = params.id;

  // Load guest resume from GuestSession API first, with localStorage fallback
  useEffect(() => {
    // console.log("🔍 Builder useEffect - isLoggedIn:", isLoggedIn, "resume keys:", Object.keys(resume).length);
    // console.log("🔍 Current resume in store:", resume);

    // Skip loading if logged in (guest session resume is not used)
    if (isLoggedIn) {
      if (window.location.pathname.includes("/builder/try")) {
        const current = useResumeStore.getState().resume;
        useResumeStore.setState({ resume: applyPendingSummaryToResumeDto(current) });
      }
      return;
    }

    // For guest users with specific ID, always try to load from server
    if (isGuestResumeWithId && guestResumeId) {
      // console.log("🔍 Guest resume with ID detected, will load from server");
      // Don't skip based on existing resume data for guest ID routes
    } else if (
      resume.id !== "default" &&
      resume.id !== "guest-resume" &&
      Object.keys(resume).length > 0
    ) {
      // console.log("🔍 Skipping guest resume load - resume already loaded with ID:", resume.id);
      return;
    }

    const loadGuestResume = async () => {
      try {
        let parsedData = defaultResumeData;
        let guestResume: any;

        // If this is a guest resume with specific ID, load it from GuestSession
        if (isGuestResumeWithId && guestResumeId) {
          console.log(`🔍 Loading guest resume with ID: ${guestResumeId}`);

          // Get template parameter from URL (validated)
          const urlParams = new URLSearchParams(window.location.search);
          const templateParam = validateTemplate(urlParams.get("template"));

          try {
            const sessionResponse = await GuestSessionService.getResumeData();

            // Safety check: ensure sessionResponse is valid
            if (!sessionResponse) {
              console.log("❌ No response from getResumeData");
              guestResume = createGuestResume();
            } else if (sessionResponse.success && sessionResponse.resumeData) {
              const savedResume = sessionResponse.resumeData;
              console.log("📥 Saved resume structure:", {
                id: savedResume.id,
                hasData: !!savedResume.data,
                keys: Object.keys(savedResume),
              });

              // Check if the saved resume matches the requested ID
              if (savedResume.id === guestResumeId) {
                console.log("✅ Found matching guest resume in session");

                // Ensure the resume has the correct structure
                if (savedResume.data) {
                  // Resume is already in correct format
                  guestResume = savedResume;

                  // Apply template parameter if present
                  if (templateParam && guestResume.data?.metadata) {
                    console.log(
                      "🎨 Applying template from URL to loaded guest resume:",
                      templateParam,
                    );
                    guestResume.data.metadata.template = templateParam;
                  }
                } else {
                  // Resume data might be flattened, reconstruct it
                  console.log("🔄 Reconstructing resume structure");
                  const metadata = savedResume.metadata || defaultResumeData.metadata;

                  // Apply template parameter if present
                  if (templateParam) {
                    console.log(
                      "🎨 Applying template from URL to reconstructed resume:",
                      templateParam,
                    );
                    metadata.template = templateParam;
                  }

                  guestResume = {
                    ...savedResume,
                    data: {
                      basics: savedResume.basics || defaultResumeData.basics,
                      sections: savedResume.sections || defaultResumeData.sections,
                      metadata,
                    },
                  };
                }
              } else {
                console.log("❌ Requested guest resume ID not found in session");
                console.log("Looking for:", guestResumeId, "Found:", savedResume.id);
                guestResume = createGuestResume();
              }
            } else {
              console.log("❌ No guest resume data found in session");
              guestResume = createGuestResume();
            }
          } catch (error) {
            console.error("❌ Failed to load guest resume by ID:", error);
            guestResume = createGuestResume();
          }
        } else {
          // Original fallback logic for /builder/try
          const urlParams = new URLSearchParams(window.location.search);
          const dataParam = urlParams.get("data");
          const langParam = urlParams.get("lang");

          if (dataParam === "1") {
            // Use localized sample data when data=1 is in URL
            const locale = mapLangParam(langParam);
            console.log("📋 Loading sample data for locale:", locale);
            parsedData = getSampleResumeForLocale(locale);
          } else {
            const loadedData = await GuestResumeSync.loadResumeData();
            if (loadedData) {
              parsedData = loadedData;
            }
          }
        }

        // If we already have a guestResume from the ID-based load, use it
        if (guestResume) {
          const withPendingSummary = applyPendingSummaryToResumeDto(guestResume);
          // Set the resume data in the store
          useResumeStore.setState({ resume: withPendingSummary });
          console.log("✅ Guest resume loaded by ID:", withPendingSummary);

          // IMPORTANT: Prevent the resume store from saving back to localStorage/server
          // This prevents overwriting the properly formatted data
          console.log("🛡️ Guest resume loaded from server, preventing auto-save for initial load");

          // Force sync to iframe after a short delay to ensure iframe is ready
          setTimeout(() => {
            const frameRef = useBuilderStore.getState().frame.ref;
            if (frameRef?.contentWindow && withPendingSummary.data) {
              // Ensure data structure is complete before sending
              const validatedData = {
                ...withPendingSummary.data,
                basics: withPendingSummary.data.basics || defaultResumeData.basics,
                sections: withPendingSummary.data.sections || defaultResumeData.sections,
                metadata: withPendingSummary.data.metadata || defaultResumeData.metadata,
              };

              const message = { type: "SET_RESUME", payload: validatedData };
              frameRef.contentWindow.postMessage(message, "*");
              console.log("📤 Sent validated guest resume to iframe");
            }
          }, 500);

          return;
        }

        // Process the loaded data (fallback for /builder/try)
        if (parsedData && parsedData !== defaultResumeData) {
          try {
            console.log("🔄 Processing guest resume data:", parsedData);

            // Get template parameter from URL (validated)
            const urlParams = new URLSearchParams(window.location.search);
            const templateParam = validateTemplate(urlParams.get("template"));

            // Check if it's in ImportResumeDto format (new format from our fix)
            if (
              "data" in parsedData &&
              parsedData.data &&
              "title" in parsedData &&
              parsedData.title &&
              ((parsedData.data as any).basics ||
                (parsedData.data as any).sections ||
                (parsedData.data as any).metadata)
            ) {
              console.log("📦 Data is in ImportResumeDto format, creating guest resume");
              const resumeData = parsedData.data as any;

              // Apply template parameter if present
              if (templateParam && resumeData.metadata) {
                console.log("🎨 Applying template from URL to loaded resume:", templateParam);
                resumeData.metadata.template = templateParam;
              }

              guestResume = {
                id: "guest-resume",
                userId: "guest",
                title: ("title" in parsedData ? parsedData.title : undefined) || t`Guest Resume`,
                slug: ("slug" in parsedData ? parsedData.slug : undefined) || "guest-resume",
                visibility: "private",
                locked: false,
                createdAt: new Date(),
                updatedAt: new Date(),
                data: resumeData,
              } as ResumeDto;
            }
            // Legacy: If parsedData looks like raw ResumeData (old format)
            else if (parsedData.basics && parsedData.sections && parsedData.metadata) {
              console.log("📄 Data is in legacy ResumeData format, wrapping");
              guestResume = createGuestResume(parsedData);
            }
            // Legacy: If it's already wrapped as ResumeDto
            else if (
              "data" in parsedData &&
              parsedData.data &&
              (parsedData.data as any)?.basics &&
              (parsedData.data as any).sections &&
              (parsedData.data as any).metadata
            ) {
              console.log("📋 Data is already in ResumeDto format");
              guestResume = { ...parsedData } as any;

              // Apply template parameter if present
              if (templateParam && guestResume.data?.metadata) {
                console.log("🎨 Applying template from URL to loaded resume:", templateParam);
                guestResume.data.metadata.template = templateParam;
              }
            } else {
              // Fallback to merging with defaultResumeData
              console.log("🔧 Using fallback data creation");
              guestResume = createGuestResume(parsedData);
            }
          } catch (error) {
            console.error("Failed to parse guest resume data:", error);
            guestResume = createGuestResume();
          }
        } else {
          guestResume = createGuestResume();
        }

        guestResume = applyPendingSummaryToResumeDto(guestResume);

        // Set the resume data in the store
        useResumeStore.setState({ resume: guestResume });

        // Ensure resume data is properly initialized before migrating signature
        if (guestResume.data) {
          // Migrate signature from metadata to sections
          migrateSignature(useResumeStore.getState());
          // Ensure signature section is in the layout
          if (guestResume.data.metadata?.layout) {
            let signatureIncluded = false;
            for (const page of guestResume.data.metadata.layout) {
              for (const column of page) {
                if (column.includes("signature")) {
                  signatureIncluded = true;
                  break;
                }
              }
              if (signatureIncluded) break;
            }
            if (
              !signatureIncluded &&
              guestResume.data.metadata.layout.length > 0 &&
              guestResume.data.metadata.layout[0].length > 0
            ) {
              const updatedResume = { ...guestResume };
              updatedResume.data.metadata.layout[0][0].push("signature");
              useResumeStore.setState({ resume: updatedResume });
            }
          }
        }
      } catch (error) {
        console.error("Error loading guest resume:", error);
        useResumeStore.setState({ resume: applyPendingSummaryToResumeDto(createGuestResume()) });
      }
    };

    // Call the async function
    loadGuestResume();
  }, [isLoggedIn, isGuestResumeWithId, guestResumeId]);

  const syncResumeToArtboard = useCallback(() => {
    const sendMessage = (retries = 10) => {
      if (!frameRef?.contentWindow) {
        if (retries > 0) {
          setTimeout(() => {
            sendMessage(retries - 1);
          }, 200);
        }
        return;
      }

      try {
        // Enhanced data validation for guest users
        if (!resume.data) {
          console.warn("⚠️ No resume data available, skipping sync");
          return;
        }

        // Ensure all required sections exist with proper structure
        const validatedData = {
          ...resume.data,
          basics: resume.data.basics || defaultResumeData.basics,
          sections: resume.data.sections || defaultResumeData.sections,
          metadata: resume.data.metadata || defaultResumeData.metadata,
        };

        // Log data structure for debugging guest user issues
        // console.log('📊 Syncing resume data structure:', {
        //   hasBasics: !!validatedData.basics,
        //   hasSections: !!validatedData.sections,
        //   hasMetadata: !!validatedData.metadata,
        //   sectionsKeys: Object.keys(validatedData.sections || {}),
        //   basicsKeys: Object.keys(validatedData.basics || {}),
        //   isGuest: !isLoggedIn,
        //   resumeId: resume.id,
        //   sectionsCount: Object.keys(validatedData.sections || {}).length
        // });

        const message = {
          type: "SET_RESUME",
          payload: validatedData,
          userType: userType, // Add user type for watermark detection
        };
        frameRef.contentWindow.postMessage(message, "*");
        // console.log('📤 Resume data sent to artboard with userType:', userType, '(attempts left:', retries, ')');
      } catch {
        // console.warn('⚠️ Failed to send resume data, retries left:', retries, error);
        if (retries > 0) {
          setTimeout(() => {
            sendMessage(retries - 1);
          }, 500);
        }
      }
    };

    // FIX: Use setTimeout instead of setImmediate (setImmediate is not available in browsers)
    setTimeout(() => { sendMessage(10); }, 0);
  }, [frameRef, resume.data, isLoggedIn, userType]);

  // Send resume data to iframe on initial load
  useEffect(() => {
    if (!frameRef) return;

    frameRef.addEventListener("load", syncResumeToArtboard);

    return () => {
      frameRef.removeEventListener("load", syncResumeToArtboard);
    };
  }, [frameRef, syncResumeToArtboard]);

  // Get the setIsArtboardLoading function from the builder store
  const setIsArtboardLoading = useBuilderStore((state) => state.setIsArtboardLoading);

  // Listen for messages from artboard (ARTBOARD_READY and RESUME_RENDERED)
  useEffect(() => {
    const handleArtboardMessage = (event: MessageEvent) => {
      // Allow localhost origins in development
      const isDevelopment = process.env.NODE_ENV === "development";
      const isLocalhost = event.origin.includes("localhost") || event.origin.includes("127.0.0.1");

      if (!isDevelopment && event.origin !== window.location.origin) return;
      if (isDevelopment && !isLocalhost && event.origin !== window.location.origin) return;

      // Handle ARTBOARD_READY signal - immediately sync resume data
      if (event.data.type === "ARTBOARD_READY") {
        console.log("✅ Artboard is ready, sending resume data immediately");
        syncResumeToArtboard();
      }

      if (event.data.type === "RESUME_RENDERED") {
        console.log("✅ Artboard signaled resume is rendered, hiding loading overlay");
        setIsArtboardLoading(false);
      }
    };

    window.addEventListener("message", handleArtboardMessage);
    return () => window.removeEventListener("message", handleArtboardMessage);
  }, [setIsArtboardLoading, syncResumeToArtboard]);

  // Enhanced iframe loading check with mobile optimization
  useEffect(() => {
    if (!frameRef) return;

    let attempts = 0;
    const maxAttempts = 60; // 6 seconds for mobile devices
    let hasSucceeded = false;

    const interval = setInterval(() => {
      attempts++;

      try {
        const isComplete = frameRef?.contentWindow?.document?.readyState === "complete";

        if (isComplete && !hasSucceeded) {
          syncResumeToArtboard();
          console.log("✅ Iframe ready, synced resume data");
          hasSucceeded = true;
          clearInterval(interval);

          // FIX: Safety fallback - hide loading overlay after 5s if RESUME_RENDERED never arrives
          setTimeout(() => {
            const stillLoading = useBuilderStore.getState().isArtboardLoading;
            if (stillLoading) {
              console.warn("⚠️ RESUME_RENDERED not received after 5s, forcing loading overlay off");
              setIsArtboardLoading(false);
            }
          }, 5000);
        } else if (attempts >= maxAttempts) {
          console.warn("⚠️ Iframe load timeout, forcing sync");
          syncResumeToArtboard();
          // Fallback: Hide loading overlay on timeout if artboard didn't signal
          setIsArtboardLoading(false);
          clearInterval(interval);
        }

        // Additional sync attempts for mobile reliability
        if ((attempts === 15 || attempts === 30) && !hasSucceeded) {
          console.log(`🔄 Mobile retry sync attempt ${attempts}`);
          syncResumeToArtboard();
        }
      } catch (err) {
        // Cross-origin error handling - enhanced for mobile
        if (attempts > 10 && !hasSucceeded) {
          console.log("🌐 Cross-origin iframe detected, attempting sync anyway");
          syncResumeToArtboard();
          if (attempts > 25) {
            hasSucceeded = true;
            setIsArtboardLoading(false); // Fix: Hide loading overlay for cross-origin iframes
            clearInterval(interval);
          }
        }
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [frameRef, syncResumeToArtboard, setIsArtboardLoading]);

  // Set El Messiri font for new Arabic resumes if not already set
  useEffect(() => {
    if (!resume?.data?.metadata?.typography) return;
    const locale = resume.data.metadata.locale.toLowerCase() || "";
    const isArabic = locale === "ar" || locale === "arabic" || locale.startsWith("ar-");
    const currentFont = resume.data.metadata.typography.font.family;
    const arabicFonts = [
      "Tajawal",
      "Cairo",
      "Amiri",
      "Changa",
      "El Messiri",
      "Harmattan",
      "Lateef",
      "Lalezar",
      "Markazi Text",
      "Reem Kufi",
      "Scheherazade New",
      "Noto Naskh Arabic",
      "Noto Sans Arabic",
      "Almarai",
      "Aref Ruqaa",
      "Baloo Bhaijaan 2",
      "Mada",
      "Mirza",
      "Rakkas",
      "Vazirmatn",
    ];
    if (isArabic && (!currentFont || !arabicFonts.includes(currentFont))) {
      setValue("metadata.typography.font.family", "El Messiri");
      setValue("metadata.typography.font.subset", "latin");
      setValue("metadata.typography.font.variants", ["regular"]);
      setValue("metadata.typography.font.size", 14);
    }
  }, [resume?.data?.metadata?.locale]);

  // Ensure mostProudOf section exists in layout for existing resumes
  useEffect(() => {
    if (!resume?.data?.metadata?.layout) return;
    if ((resume as any).type === "coverLetter") return;
    const layout = resume.data.metadata.layout;

    let mostProudOfIncluded = false;
    for (const page of layout) {
      for (const column of page) {
        if (column.includes("mostProudOf")) {
          mostProudOfIncluded = true;
          break;
        }
      }
      if (mostProudOfIncluded) break;
    }

    if (!mostProudOfIncluded && layout.length > 0 && layout[0].length > 1) {
      const layoutCopy = JSON.parse(JSON.stringify(layout));
      const mainColumn = layoutCopy[0][0];
      mainColumn.push("mostProudOf");
      setValue("metadata.layout", layoutCopy);
    }
  }, [resume?.data?.metadata?.layout, setValue]);

  // Ensure myMind section exists in layout for existing resumes
  useEffect(() => {
    if (!resume?.data?.metadata?.layout) return;
    if ((resume as any).type === "coverLetter") return;
    const layout = resume.data.metadata.layout;

    let myMindIncluded = false;
    for (const page of layout) {
      for (const column of page) {
        if (column.includes("myMind")) {
          myMindIncluded = true;
          break;
        }
      }
      if (myMindIncluded) break;
    }

    if (!myMindIncluded && layout.length > 0 && layout[0].length > 1) {
      const layoutCopy = JSON.parse(JSON.stringify(layout));
      const mainColumn = layoutCopy[0][0];
      mainColumn.push("myMind");
      setValue("metadata.layout", layoutCopy);
    }
  }, [resume?.data?.metadata?.layout, setValue]);

  return (
    <>
      <Helmet>
        <title>
          {title} - {t`Stylion`}
        </title>
      </Helmet>

      <ResponsivePreview
        frameRef={frameRef}
        setFrameRef={setFrameRef}
        resumeId={resume.id || "guest-resume"}
      />

      {/* Dialogs */}
      <DeleteSectionDialog />
    </>
  );
};

export const builderLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const id = params.id!;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    // console.log("🔍 Builder loader fetched resume:", resume);
    // console.log("🔍 Resume locale from DB:", resume.data?.metadata?.locale);
    // console.log("🔍 Resume data structure:", JSON.stringify(resume.data, null, 2));

    // Check for transition data from the signup process (legacy fallback)
    // NOTE: This should not be needed anymore since we create resumes with complete data
    try {
      const transitionData = localStorage.getItem("resume-transition-data");
      if (transitionData && resume.data) {
        console.log("🔄 Found legacy transition data, applying as fallback...", transitionData);
        const preservedContent = JSON.parse(transitionData);

        // Apply preserved content - these should be type-compatible fields
        if (preservedContent.sections) {
          resume.data.sections = preservedContent.sections;
        }

        if (preservedContent.basics) {
          resume.data.basics = preservedContent.basics;
        }

        if (preservedContent.metadata) {
          // Apply each property individually to avoid type issues
          if (preservedContent.metadata.template) {
            resume.data.metadata.template = preservedContent.metadata.template;
          }

          if (preservedContent.metadata.locale) {
            resume.data.metadata.locale = preservedContent.metadata.locale;
          }

          if (preservedContent.metadata.theme) {
            resume.data.metadata.theme = preservedContent.metadata.theme;
          }

          if (preservedContent.metadata.typography) {
            resume.data.metadata.typography = preservedContent.metadata.typography;
          }

          if (preservedContent.metadata.page) {
            resume.data.metadata.page = preservedContent.metadata.page;
          }
        }

        // Remove the transition data as it's no longer needed
        localStorage.removeItem("resume-transition-data");
        console.log("✅ Transition data applied and cleared");
      }
    } catch (error) {
      console.error("❌ Error handling resume transition data:", error);
      // Continue with the original resume data if there's an error
    }

    // AUTO-REPAIR: Fix corrupted resumes that have coverLetterContent in their layout
    // This happened due to a bug where switching to Cover Letter tab modified the actual resume data
    const isRegularResume = !resume.type || resume.type === "resume";
    const layoutStr = JSON.stringify(resume.data?.metadata?.layout || []);
    const hasCoverLetterContent = layoutStr.includes("coverLetterContent");
    
    if (isRegularResume && hasCoverLetterContent) {
      console.warn(`Auto-repairing corrupted resume layout for ${resume.id}`);
      // Reset to default resume layout
      const defaultResumeLayout = [
        [
          ["summary", "experience", "education", "projects", "references", "mostProudOf", "myMind", "signature"],
          ["profiles", "skills", "certifications", "interests", "hobbies", "languages", "awards", "volunteer", "publications"],
        ],
      ];
      resume.data.metadata.layout = defaultResumeLayout;
      // Also reset template if it was changed to azurill (cover letter template)
      if (resume.data.metadata.template === "clean") {
        resume.data.metadata.template = "framed";
      }
    }
    
    useResumeStore.setState({ resume: applyPendingSummaryToResumeDto(resume) });
    useResumeStore.temporal.getState().clear();

    // Ensure resume data is properly initialized before migrating signature
    if (resume && resume.data) {
      // Migrate signature from metadata to sections
      migrateSignature(useResumeStore.getState());

      // Ensure signature section is in the layout
      if (resume.data.metadata && resume.data.metadata.layout) {
        let signatureIncluded = false;

        // Check if signature is already in the layout
        for (const page of resume.data.metadata.layout) {
          for (const column of page) {
            if (column.includes("signature")) {
              signatureIncluded = true;
              break;
            }
          }
          if (signatureIncluded) break;
        }

        // If not included, add it to the layout
        if (
          !signatureIncluded &&
          resume.data.metadata.layout.length > 0 &&
          resume.data.metadata.layout[0].length > 0
        ) {
          // Need to update resume in the store to include signature
          const updatedResume = { ...resume };
          updatedResume.data.metadata.layout[0][0].push("signature");
          useResumeStore.setState({ resume: updatedResume });
        }
      }
    }

    return resume;
  } catch {
    const storedLocale = typeof window !== "undefined" ? window.localStorage.getItem("locale") : null;
    return redirect(storedLocale ? addLocaleToPath("/dashboard", storedLocale) : "/dashboard");
  }
};
